import { inject } from '@angular/core';
import { EntityStatus } from '@core';
import { DeviceServiceV2 } from '@devices';
import { type Breadcrumb, type BreadcrumbsMap } from '@devices/models/breadcrumbs';
import { type LocationData, LocationService,type Space, SpaceType } from '@locations';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';

interface DeviceDetailsBreadcrumbsState {
  breadcrumbs: BreadcrumbsMap | null;
  buildings: LocationData[];
  devices: Breadcrumb[];
  floors: Space[];
  isLoading: boolean;
  rooms: Space[];
}

export const DeviceDetailsBreadcrumbsComponentStore = signalStore(
  withState<DeviceDetailsBreadcrumbsState>({
    breadcrumbs: null,
    buildings: [],
    devices: [],
    floors: [],
    isLoading: false,
    rooms: [],
  }),
  withMethods(
    (
      store,
      deviceService = inject(DeviceServiceV2),
      locationService = inject(LocationService),
    ) => ({
      loadBreadcrumbs(deviceId: string) {
        deviceService.breadcrumbsMap(deviceId).subscribe(breadcrumbs => {
          patchState(store, { breadcrumbs, buildings: [], devices: [], floors: [], rooms: [] });
        });
      },
      loadBuildings() {
        if (store.buildings().length > 0) {
          return;
        }

        patchState(store, { isLoading: true });
        locationService.getLocationsList(false).subscribe(buildings => {
          patchState(store, { buildings, isLoading: false });
        });
      },
      loadDevices() {
        const breadcrumbs = store.breadcrumbs();

        if (store.devices().length > 0 || !breadcrumbs?.room) {
          return;
        }

        patchState(store, { isLoading: true });
        deviceService.list(
          {
            filters:
              {
                space: breadcrumbs.room.id,
                status: [EntityStatus.Active, EntityStatus.Paused, EntityStatus.Deactivated],
              },
          }).subscribe(response => {
          patchState(store, {
            devices: response.data.map(d => ({
              friendlyName: d.deviceName,
              id: d.id,
              name: d.deviceName,
            })),
            isLoading: false,
          });
        });
      },
      loadFloors() {
        const breadcrumbs = store.breadcrumbs();

        if (store.floors().length > 0 || !breadcrumbs?.location) {
          return;
        }

        patchState(store, { isLoading: true });
        locationService.getSpacesList(breadcrumbs.location.id, { type: SpaceType.floor }).subscribe(floors => {
          patchState(store, { floors, isLoading: false });
        });
      },
      loadRooms() {
        const breadcrumbs = store.breadcrumbs();

        if (store.rooms().length > 0 || !breadcrumbs?.floor) return;

        patchState(store, { isLoading: true });
        locationService
          .getSpacesList(breadcrumbs.location.id, { parentId: breadcrumbs.floor.id, type: SpaceType.room })
          .subscribe(rooms => {
            patchState(store, { isLoading: false, rooms });
          });
      },
    }),
  ),
);
